import { Provider } from '@angular/core';
import { SSE_LIVE_CONNECTION_CONFIG, SseLiveConnectionConfig } from '@tuain/ng-forms';
import { EventManagerService } from './services/event-manager.service';
import { AppLoggerService } from './services/app-logger.service';
import { buildBaseUrl } from './utils/url.utils';
import { defaultConfig } from '../app.config';

/**
 * Allow-list de eventos que pueden originarse desde el servidor.
 *
 * Sin esta lista, un backend (o MITM) podría inyectar eventos de lifecycle
 * (`sessionEnded`, `restartApp`, `compromisedDevice`, `notAuthorized`, ...) y
 * dispararlos en la app. Solo eventos informativos de origen razonable del
 * servidor; ampliar con cuidado — nunca incluir eventos de control de sesión/app.
 */
const ALLOWED_SERVER_EVENTS = [
  'notification',
  'invasiveNotification',
  'closeInvasiveNotification',
  'enableNotifications',
  'chat',
  'chatMessage',
  'messageRead',
  'menuObtained',
  'profileObtained',
  'pendingNotifications',
  'pendingTickets',
  'agentStatus',
  'fileUploadWidget',
  'sessionClosedByServer',
];

/**
 * Provee la configuración del canal SSE para esta app. El servicio de la lib
 * (`SseLiveConnectionService`) reemite los eventos validados al bus de la app
 * a través del callback `emit`, y registra vía el `AppLoggerService`.
 */
export function provideSseLiveConnection(): Provider {
  return {
    provide: SSE_LIVE_CONNECTION_CONFIG,
    useFactory: (eventManager: EventManagerService, logger: AppLoggerService): SseLiveConnectionConfig => ({
      buildUrl: () => buildBaseUrl((defaultConfig as any)?.serverEntryPoint),
      getAuthToken: (sessionData: any) => sessionData?.sessionId ?? '',
      allowedEvents: ALLOWED_SERVER_EVENTS,
      emit: (eventClass, eventData) => eventManager.next(eventClass, eventData),
      // La lib emite este evento SOLO al detectar `sessionReplaced` (otra sesión del mismo
      // usuario). Lo redirigimos a un evento propio para mostrar un mensaje específico antes
      // del logout, en vez del `sessionEnded` genérico/silencioso. El cierre real lo dispara
      // AppComponent emitiendo `sessionEnded` tras avisar al usuario.
      sessionEndedEventName: defaultConfig.appEvents.sessionReplaced.name,
      // Frente C (SSE): el gateway envía el cierre de sesión con eventClass
      // 'sessionClosedByServer' (ver session-notify.js), que ya viaja en el allow-list de
      // arriba. La lib lo re-emite tal cual vía `emit`, sin necesitar config dedicada (a
      // diferencia de `sessionReplaced`, que sí es un caso especial hardcodeado en la lib).
      logger: {
        debug: (message: string, ...args: any[]) => logger.debug(message, ...args),
        warn: (message: string, ...args: any[]) => logger.warn(message, ...args),
      },
    }),
    deps: [EventManagerService, AppLoggerService],
  };
}
